import {
  WorkplaceRequestCreateChildTicketConfig,
  WorkplaceRequestOccupiedResourceAction,
  WorkplaceRequestReleasedResourceAction,
  WorkplaceRequestTypeField,
  WorkplaceRequestTypeSummary,
  WorkplaceRequestWorkflowAction
} from "./workplace-request-types";

export type WorkplaceRequestUser = {
  id: string;
  email: string;
  name: string;
  title: string;
  phone: string;
  profile_photo_url: string;
  default_organization_id: string;
  roles: string[];
  role_permissions?: Record<string, unknown>;
  default_office_id: string;
  time_zone: string;
  date_format: string;
};

export type WorkplaceRequestStatus = "open" | "closed" | "pending" | "archived";

/**
 * Approval state of a request whose type has `requires_approval` enabled.
 * Mirrors the backend `ApprovalStatus` enum.
 */
export type WorkplaceRequestApprovalStatus =
  | "pending"
  | "approved"
  | "rejected";

/** Action taken when responding to a pending approval. */
export type WorkplaceRequestApprovalResponse = "approve" | "reject";

export type WorkplaceRequestSubStatus = {
  id: string;
  name: string;
  order: number;
};

/**
 * Value stored for a `file_upload` form field once read back from the API
 * (the path is exchanged for a short-lived signed `url`).
 */
export type WorkplaceRequestFileUploadValue = {
  url: string;
  filename?: string;
};

/** Value submitted for a `file_upload` form field (the uploaded object path). */
export type WorkplaceRequestFileUploadInput = {
  path: string;
  filename?: string;
};

/** Response of the ticket-file upload-URL endpoint (`POST /v1/tickets/uploads`). */
export type WorkplaceRequestUploadResponse = {
  upload_url: string;
  path: string;
  access_url: string;
};

/** The most days one `reservation` answer may name. */
export const WORKPLACE_REQUEST_RESERVATION_MAX_DATES = 15;

/** The most attendees one `reservation` answer may carry. */
export const WORKPLACE_REQUEST_RESERVATION_MAX_ATTENDEES = 500;

/**
 * One attendee of a reservation, as it is written. Exactly one of `user_id`
 * and `email` is set — the `never` on the other key is what makes sending both
 * a compile error rather than a 400.
 *
 * A `user_id` has to resolve to a visible organization user or core rejects
 * the whole answer. An `email` need not match anyone: one that matches no
 * organization user becomes a guest when the reservation is booked. Core trims
 * and lower-cases it.
 */
export type WorkplaceRequestReservationAttendeeInput =
  | { user_id: string; email?: never }
  | { email: string; user_id?: never };

/**
 * An attendee read back from a request: the identifier it was submitted with,
 * plus the organization user it resolves to — matched by id, or by an
 * organization user's email. `user` is `null` for an attendee that resolves to
 * nobody: a deleted user, or an external guest's address.
 */
export type WorkplaceRequestReservationAttendee =
  WorkplaceRequestReservationAttendeeInput & {
    user: WorkplaceRequestUser | null;
  };

/** How a reservation repeats. Never combined with more than one day. */
export type WorkplaceRequestReservationRecurrence = {
  /** RRULE string, e.g. `FREQ=WEEKLY;BYDAY=TU`. */
  rrule: string;
  /** First day the rule applies, as `YYYY-MM-DD`. */
  start_date: string;
  /**
   * Last day the rule applies, as `YYYY-MM-DD`. Absent or `null` for an
   * open-ended series; never before `start_date`.
   */
  end_date?: string | null;
};

/**
 * What the form sends for a `reservation` field. Nothing is booked when the
 * request is submitted: the answer is stored on the ticket, and a
 * `reservations:create` workflow turns it into reservations on approval.
 *
 * Core validates the object as a whole and answers a flat `400`
 * `invalid_field_value` with no per-rule detail, so these rules have to be
 * enforced client-side to be explainable at all:
 *
 * - 1 to {@link WORKPLACE_REQUEST_RESERVATION_MAX_DATES} days, each
 *   `YYYY-MM-DD` and distinct; more than one only where the field's config has
 *   `multi_day_enabled`.
 * - `end_time` strictly after `start_time`, on the same day.
 * - several days *or* a `recurrence`, never both.
 * - `recurrence` only where the config has `recurrence_enabled`, `attendees`
 *   only where it has `attendees_enabled`.
 * - a required field counts as unanswered when `dates` is empty or either time
 *   is missing.
 */
export type WorkplaceRequestReservationFieldValueInput = {
  /** The days to book, each `YYYY-MM-DD`. */
  dates: string[];
  /** 24-hour `HH:MM` (or `HH:MM:SS`), shared by every day. */
  start_time: string;
  /** 24-hour `HH:MM` (or `HH:MM:SS`), shared by every day. */
  end_time: string;
  recurrence?: WorkplaceRequestReservationRecurrence | null;
  attendees?: WorkplaceRequestReservationAttendeeInput[];
};

/**
 * A `reservation` answer read back from a request. `dates` is always present,
 * never empty and sorted ascending, and every attendee carries the user it
 * resolves to.
 */
export type WorkplaceRequestReservationFieldValue = Omit<
  WorkplaceRequestReservationFieldValueInput,
  "attendees"
> & {
  attendees?: WorkplaceRequestReservationAttendee[];
};

/**
 * Narrows an answer read off a request. Field values arrive as `unknown`, so
 * this is the only safe way in: it checks the three keys core always returns
 * for a reservation and trusts the contract for the rest.
 */
export const isWorkplaceRequestReservationFieldValue = (
  value: unknown
): value is WorkplaceRequestReservationFieldValue => {
  if (!value || typeof value !== "object") return false;
  const candidate = value as Record<string, unknown>;
  return (
    Array.isArray(candidate.dates) &&
    candidate.dates.every((date) => typeof date === "string") &&
    typeof candidate.start_time === "string" &&
    typeof candidate.end_time === "string"
  );
};

/**
 * A request's physical location, stored at the ticket level (core `TicketLocation`).
 * A discriminated union: a resource (desk/room), a floor, or a pinned point
 * (coordinates on a floor). Same shape on read and write.
 */
export type WorkplaceRequestLocationValue =
  | { type: "resource"; resource_id: string }
  | { type: "floor"; floor_id: string }
  | { type: "coordinates"; floor_id: string; x: number; y: number };

/**
 * A stored answer for a workplace request type field. Pairs the field definition
 * with the submitted value (shape depends on the field type).
 */
export type WorkplaceRequestFieldValue = WorkplaceRequestTypeField & {
  value: unknown;
};

/**
 * What the current viewer is allowed to do with a request, resolved by the
 * backend from the viewer's roles and the ticket type. Returned on every
 * request; use it to gate UI actions instead of re-deriving permissions.
 */
export type WorkplaceRequestCapabilities = {
  can_comment: boolean;
  can_edit: boolean;
  can_edit_owner: boolean;
  can_delete: boolean;
  can_approve: boolean;
  /** Whether the viewer may edit the request's custom field values. */
  can_edit_field_values: boolean;
  /** Whether the viewer may create guest share links for the request. */
  can_share: boolean;
};

/**
 * How a single workflow run ended.
 *
 * `pending` is queued and not finished — when it carries an `error` the last
 * attempt failed and will be retried. `scheduled` is deferred and waiting for
 * its `scheduled_for` instant. `skipped` means the workflow deliberately did
 * nothing, typically because a value it needed wasn't set on the ticket.
 */
export type WorkplaceRequestWorkflowRunStatus =
  | "pending"
  | "scheduled"
  | "success"
  | "failed"
  | "skipped";

/**
 * One state change a run applied to one resource. A released desk records
 * whatever the rule's `released_resource_action` actually did to it — and
 * records nothing at all when it was already in that state.
 */
export type WorkplaceRequestWorkflowResourceChangeKind =
  | "made_dedicated"
  | "removed_dedication"
  | "made_closed"
  | "made_open"
  | "made_unavailable"
  | "converted_to_workspace"
  | "capacity_increased";

export type WorkplaceRequestWorkflowResourceChange = {
  resource_id: string;
  changes: WorkplaceRequestWorkflowResourceChangeKind[];
};

/**
 * What a run of one of the four `resources:` actions did.
 *
 * `released_resource_ids` is recorded whatever the released-desk action was —
 * it names the desks the person stopped holding, which is the move itself.
 * `updated_resources` only carries desks whose state the run actually changed,
 * so a released desk missing from it was left exactly as it was.
 */
export type WorkplaceRequestResourceAssignmentRunResult = {
  /** The user the action moved. */
  user_id: string;
  /** The resource the action targeted. */
  resource_id: string;
  /**
   * The first of `replaced_user_ids`.
   * @deprecated Read `replaced_user_ids`; only runs recorded before shared
   * workspaces carry this alone.
   */
  replaced_user_id?: string;
  /** Every owner evicted from the target resource to make room for the user. */
  replaced_user_ids?: string[];
  /** Resources the user was released from by this run. */
  released_resource_ids?: string[];
  /** Every resource this run changed, target and released alike. */
  updated_resources?: WorkplaceRequestWorkflowResourceChange[];
  /** The run made no changes. */
  no_op?: boolean;
};

/**
 * What a run did, shaped by its workflow's action. The four `resources:`
 * actions and `reservations:create` expose one today; every other action —
 * and every run that recorded nothing, which is all of the pending, scheduled,
 * failed and skipped ones — reports `null`.
 */
export type WorkplaceRequestWorkflowRunResult =
  | WorkplaceRequestResourceAssignmentRunResult
  | WorkplaceRequestReservationRunResult
  | null;

/**
 * One execution of a workflow owned by the request's type. Returned newest
 * first on the single-request endpoint, and only to callers who manage the
 * type — everyone else sees an empty list.
 */
export type WorkplaceRequestWorkflowRun = {
  id: string;
  workflow: {
    id: string;
    name: string;
    /** The action it performs, e.g. `resources:set_primary_assignment`. */
    action: WorkplaceRequestWorkflowAction | string;
  };
  status: WorkplaceRequestWorkflowRunStatus;
  /**
   * What the run did. Narrow it with `workflow.action` rather than by probing
   * keys — a future action may expose a result of its own.
   */
  result: WorkplaceRequestWorkflowRunResult;
  /** When a deferred run is due; `null` for runs that executed immediately. */
  scheduled_for: string | null;
  /** Why the run failed or was skipped; `null` otherwise. */
  error: string | null;
  created: string;
};

/** One reservation a `reservations:create` run booked. */
export type WorkplaceRequestBookedReservation = {
  schedule_id: string;
  start: string;
  end: string;
  /** Set when the run booked a series rather than single days. */
  recurring_task_id?: string;
};

/**
 * What a successful `reservations:create` run booked, as core recorded it:
 * the reservations themselves plus the values it booked them with, the
 * approver's overrides already applied. `attendees` is the final guest list —
 * the answer's attendees, the workflow's default attendees and whatever the
 * approver changed — which the request's own field value does not carry.
 */
export type WorkplaceRequestReservationRunResult = {
  reservations: WorkplaceRequestBookedReservation[];
  resource_id: string;
  resource_type: string;
  organizer_id: string;
  creator_id: string;
  attendees?: WorkplaceRequestReservationAttendeeInput[];
  buffer?: { before_minutes: number; after_minutes: number };
  recurrence?: WorkplaceRequestReservationRecurrence;
  online_meeting_provider?: string;
  /** Override fields core dropped because the booked resource can't take them. */
  dropped_fields?: string[];
};

/**
 * Narrows a run's `result` to the reservation shape. Only the parts read back
 * are checked; core is the only writer and its shape is fixed per action.
 */
export const isWorkplaceRequestReservationRunResult = (
  result: unknown
): result is WorkplaceRequestReservationRunResult => {
  if (typeof result !== "object" || result === null) return false;
  const candidate = result as Record<string, unknown>;
  return (
    Array.isArray(candidate.reservations) &&
    typeof candidate.resource_id === "string" &&
    typeof candidate.organizer_id === "string" &&
    (candidate.attendees === undefined || Array.isArray(candidate.attendees))
  );
};

/** One window a `reservations:create` run would book, as RFC3339 instants. */
export type WorkplaceRequestReservationPeriod = {
  start: string;
  end: string;
};

/**
 * The reservations a `reservations:create` workflow would book for a ticket,
 * every slot already resolved. Returned by `GET /v1/tickets/{id}/workflows`.
 */
export type WorkplaceRequestReservationWorkflowValues = {
  /**
   * One entry per reservation the run would book: one per day the config
   * names, or a single one covering the series when `recurrence` is set. At
   * most {@link WORKPLACE_REQUEST_RESERVATION_MAX_DATES}, and none overlapping
   * once the buffer is applied.
   *
   * The precheck runs for every period, so one unavailable day blocks the
   * whole approval.
   */
  periods: WorkplaceRequestReservationPeriod[];
  resource_id: string;
  organizer_id: string;
  title?: string;
  description?: string;
  /**
   * The config's `attendees` and `default_attendees`, already merged and
   * de-duplicated. An email matching no organization user is booked as a guest.
   */
  attendees: WorkplaceRequestReservationAttendeeInput[];
  online_meeting: boolean;
  buffer?: { before_minutes: number; after_minutes: number };
  recurrence?: WorkplaceRequestReservationRecurrence;
};

/**
 * What an approver may replace at approval time, keyed exactly like the
 * resolved values. Only the keys that actually changed are sent; core keeps
 * the resolved value for every key left out.
 *
 * The body is parsed strictly — an unknown key fails the whole approval with a
 * `400` `invalid_workflow_override`, which is what retires the old
 * `attendee_ids`. Overriding `attendees` replaces the entire list, the
 * config's `default_attendees` included: send `[]` to book with none.
 */
export type WorkplaceRequestReservationWorkflowOverride =
  Partial<WorkplaceRequestReservationWorkflowValues>;

/**
 * What an approver may replace on one of the four `resources:` actions, for
 * this approval only: the fate of the desk the move releases and, on
 * `assign_dedicated_user` alone, what happens when the target is full. The
 * configured value stands for every key left out, so send a key only when the
 * approver actually picked something else.
 */
export type WorkplaceRequestResourceAssignmentWorkflowOverride = {
  released_resource_action?: WorkplaceRequestReleasedResourceAction;
  occupied_resource_action?: WorkplaceRequestOccupiedResourceAction;
};

/**
 * One workflow's approver overrides. Which member applies is decided by the
 * workflow's action — core validates the body against the action that owns it
 * and rejects any key that action doesn't know.
 */
export type WorkplaceRequestWorkflowOverride =
  | WorkplaceRequestReservationWorkflowOverride
  | WorkplaceRequestResourceAssignmentWorkflowOverride;

/** Overrides for `POST /v1/tickets/{id}/approval`, keyed by workflow id. */
export type WorkplaceRequestWorkflowOverrides = Record<
  string,
  WorkplaceRequestWorkflowOverride
>;

/**
 * One enabled workflow of a ticket's type, with the values it would run with.
 * `resolved` is absent when core could not resolve the ticket (a referenced
 * field deleted, say), in which case the approver has to supply every value.
 */
export type WorkplaceRequestTicketWorkflow = {
  id: string;
  name: string | null;
  action_type: WorkplaceRequestWorkflowAction | string;
  resolved?: unknown;
};

export type WorkplaceRequestReservationTicketWorkflow = Omit<
  WorkplaceRequestTicketWorkflow,
  "resolved"
> & {
  action_type: "reservations:create";
  resolved?: WorkplaceRequestReservationWorkflowValues;
};

/**
 * Narrows to the reservation action. `resolved` is trusted as-is: core is the
 * only writer and its shape is fixed per action type.
 */
export const isReservationTicketWorkflow = (
  workflow: WorkplaceRequestTicketWorkflow
): workflow is WorkplaceRequestReservationTicketWorkflow =>
  workflow.action_type === "reservations:create";

/**
 * A workflow that spawns follow-up requests when the ticket is approved. The
 * action takes no approver overrides, so `resolved` is the stored config as-is.
 */
export type WorkplaceRequestCreateChildTicketTicketWorkflow = Omit<
  WorkplaceRequestTicketWorkflow,
  "resolved"
> & {
  action_type: "tickets:create_child_ticket";
  resolved?: WorkplaceRequestCreateChildTicketConfig;
};

/** Narrows to the follow-up (child ticket) action; see {@link isReservationTicketWorkflow}. */
export const isCreateChildTicketTicketWorkflow = (
  workflow: WorkplaceRequestTicketWorkflow
): workflow is WorkplaceRequestCreateChildTicketTicketWorkflow =>
  workflow.action_type === "tickets:create_child_ticket";

/** The endpoint returns a bare array, not an envelope. */
export type WorkplaceRequestTicketWorkflowsResponse =
  WorkplaceRequestTicketWorkflow[];

/**
 * A workplace request without its relations (core `Ticket`). This is the shape
 * every list endpoint returns, and the one a request's `parent`/`children`
 * carry — relations are one level deep, so a related request has none of its
 * own.
 */
export type WorkplaceRequestRelated = {
  id: string;
  display_id: string;
  title: string;
  description: string;
  type: WorkplaceRequestTypeSummary;
  priority: number;
  status: WorkplaceRequestStatus;
  sub_status: WorkplaceRequestSubStatus | null;
  office_id: string;
  /** The request's physical location, or `null` when none is set (core ticket-level). */
  location: WorkplaceRequestLocationValue | null;
  /**
   * Legacy alias for a resource-kind location. Mirrors `location` when its type is
   * `resource`; prefer reading `location`. Kept for back-compat.
   */
  resource_id: string | null;
  schedule_id: string | null;
  submitter_user: WorkplaceRequestUser;
  owner_user: WorkplaceRequestUser | null;
  created: string;
  updated: string;
  field_values: WorkplaceRequestFieldValue[];
  /** Set only when the request's type requires approval; otherwise `null`. */
  approval_status: WorkplaceRequestApprovalStatus | null;
  approval_status_at: string | null;
  approval_status_user: WorkplaceRequestUser | null;
  /** When the request was closed; `null` while open or pending. */
  resolved_at: string | null;
  capabilities: WorkplaceRequestCapabilities;
};

/**
 * A workplace request with everything the single-request endpoint adds on top
 * of {@link WorkplaceRequestRelated}. Every added field is optional because a
 * request taken off a list carries none of them: treat `undefined` as "not
 * loaded" rather than "none".
 */
export type WorkplaceRequestElement = WorkplaceRequestRelated & {
  /**
   * Runs triggered by this request, newest first. Only the single-request
   * endpoint returns it, so treat `undefined` as "not loaded" rather than
   * "none" — a request straight off a list has no runs attached.
   */
  workflow_runs?: WorkplaceRequestWorkflowRun[];
  /**
   * The request this one was spawned from, `null` when it has no parent or the
   * viewer cannot see it.
   */
  parent?: WorkplaceRequestRelated | null;
  /**
   * The requests this one spawned, ordered by display id. Only the ones the
   * viewer can see are included.
   */
  children?: WorkplaceRequestRelated[];
};

/**
 * Slim request representation returned when requests are surfaced alongside
 * other entities (core `TicketSummary`), e.g. on the gantt overview.
 */
export type WorkplaceRequestSummary = Pick<
  WorkplaceRequestElement,
  | "id"
  | "display_id"
  | "title"
  | "type"
  | "status"
  | "priority"
  | "office_id"
  | "schedule_id"
  | "location"
  | "created"
>;

export type WorkplaceRequestsResponse = {
  cursor: string | null;
  elements: WorkplaceRequestElement[];
  /** Total requests matching the applied filters, ignoring pagination. */
  total: number;
};

/** Count of requests of a single type within a dashboard stat category. */
export type WorkplaceRequestTypeCount = {
  type: WorkplaceRequestTypeSummary;
  count: number;
};

/** A dashboard stat category: its total plus a per-type breakdown. */
export type WorkplaceRequestStatCategory = {
  total: number;
  by_type: WorkplaceRequestTypeCount[];
};

/**
 * Dashboard counts returned by `GET /v1/tickets/stats`, scoped to the caller's
 * visibility. Each category is broken down by ticket type.
 */
export type WorkplaceRequestStatsResponse = {
  /** Requests currently pending approval. */
  pending_approval: WorkplaceRequestStatCategory;
  /** Open or pending requests that are not pending approval. */
  unresolved: WorkplaceRequestStatCategory;
  /** Unresolved requests with no owner assigned. */
  needs_triage: WorkplaceRequestStatCategory;
  /** Pending requests that are not pending approval. */
  in_progress: WorkplaceRequestStatCategory;
  /** Closed requests that are not pending approval. */
  resolved: WorkplaceRequestStatCategory;
  /** Requests owned by the authenticated user. */
  assigned_to_me: WorkplaceRequestStatCategory;
  /** Requests submitted by the authenticated user. */
  my_requests: WorkplaceRequestStatCategory;
};

/**
 * Narrows results by approval state. `pending_only` returns only requests
 * pending approval; `pending_exclude` excludes them.
 */
export type WorkplaceRequestApprovalFilter = "pending_only" | "pending_exclude";

export type WorkplaceRequestsParams = {
  tz: string;
  type_id?: string[];
  status?: WorkplaceRequestStatus[];
  office_id?: string[];
  resource_id?: string[];
  submitter_user_id?: string[];
  owner_user_id?: string[];
  managed?: boolean;
  /** Filter by approval state; used to reproduce the dashboard stat categories. */
  approval_filter?: WorkplaceRequestApprovalFilter;
  /** When true, returns only requests without an owner. Cannot be combined with `owner_user_id`. */
  unassigned?: boolean;
  query?: string;
  cursor?: string;
  limit?: number;
  start?: string;
  end?: string;
};

export type WorkplaceRequestFieldValueInput = {
  field_id: string;
  value: unknown;
};

export type CreateWorkplaceRequestParams = {
  tz?: string;
  office_id: string;
  status: WorkplaceRequestStatus;
  /**
   * Required unless the type carries a `title_template`, in which case core
   * renders the title itself and anything sent here is discarded.
   */
  title?: string;
  type_id: string;
  description?: string;
  owner_user_id?: string;
  priority?: 1 | 2 | 3;
  /** The request's physical location (resource, floor, or pinned point). */
  location?: WorkplaceRequestLocationValue;
  /**
   * Legacy alias for a resource-kind location; do not combine with `location`.
   * Prefer `location`.
   */
  resource_id?: string;
  schedule_id?: string;
  field_values?: WorkplaceRequestFieldValueInput[];
  /**
   * Files previously uploaded through the ticket uploads endpoint, e.g. photos
   * of the problem. Core adds them as the request's first comment.
   */
  attachments?: { path: string; filename: string }[];
};

export type UpdateWorkplaceRequestParams = {
  title?: string | null;
  description?: string | null;
  office_id?: string | null;
  owner_user_id?: string | null;
  priority?: 1 | 2 | 3 | null;
  /**
   * Updated physical location. Omit to leave unchanged; `null` clears it.
   */
  location?: WorkplaceRequestLocationValue | null;
  /**
   * Legacy alias for a resource-kind location; do not combine with `location`.
   * `null` or empty string clears the location. Prefer `location`.
   */
  resource_id?: string | null;
  schedule_id?: string | null;
  /**
   * A base status (`open`/`closed`/`pending`/`archived`) OR a ticket-type sub-status id.
   * The backend resolves a sub-status id to its base status automatically.
   */
  status?: WorkplaceRequestStatus | (string & {}) | null;
  type_id?: string | null;
  field_values?: WorkplaceRequestFieldValueInput[];
  update_comment?: string | null;
};

/**
 * `PUT /tickets` body: one update per ticket, keyed by ticket id. Each entry
 * takes the same body as a single update. At most 100 tickets per call.
 */
export type UpdateWorkplaceRequestsBatchParams = {
  tickets: Record<string, UpdateWorkplaceRequestParams>;
};

/**
 * One entry of a batch update: `ticket` on success, `error` on a per-ticket
 * failure. The error code is one of the single-update codes, or `not_found`.
 */
export type WorkplaceRequestBatchUpdateResult = {
  ticket_id: string;
  ticket?: WorkplaceRequestElement;
  error?: { code: string; message: string };
};

export type WorkplaceRequestsBatchUpdateResponse = {
  results: WorkplaceRequestBatchUpdateResult[];
};

/**
 * A guest share link for a request. Its `token` is presented via the
 * `X-Tactic-Ticket-Share-Code` header to grant a named guest scoped access to
 * the single shared request without a Tactic account.
 */
export type WorkplaceRequestShareLink = {
  id: string;
  ticket_id: string;
  token: string;
  name: string;
  email: string;
  /** ISO timestamp when the link expires; `null` when it never expires. */
  expires_at: string | null;
  created: string;
};

export type WorkplaceRequestShareLinksResponse = {
  cursor: string | null;
  elements: WorkplaceRequestShareLink[];
};

export type WorkplaceRequestShareLinksParams = {
  cursor?: string;
  limit?: number;
};

export type CreateWorkplaceRequestShareLinkParams = {
  name: string;
  email: string;
  /** Omit or set to `null` for a link that never expires. */
  expires_at?: string | null;
};

/**
 * Guest-side update payload for a shared request. A guest may only change the
 * request's custom field values.
 */
export type UpdateSharedWorkplaceRequestParams = {
  field_values?: WorkplaceRequestFieldValueInput[];
};
